<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import MemberHeader from "@/components/member/MemberHeader.vue";
import GovernmentSidebar from "@/components/government/GovernmentSidebar.vue";

const { t } = useI18n();

const router = useRouter();

const stats = ref({
  registeredProjects: 156,
  pendingApprovals: 12,
  creditsIssued: "45,230",
  activeTransfers: 8,
});

const pendingApprovals = ref([
  {
    id: "PROJ-234",
    name: "Coastal Mangrove Restoration",
    developer: "Ocean Conservation Ltd",
    submitted: "2024-01-10",
    type: "AFOLU",
    status: "Under Review",
  },
  {
    id: "PROJ-235",
    name: "Industrial Energy Efficiency",
    developer: "Manufacturing Corp",
    submitted: "2024-01-12",
    type: "Energy Efficiency",
    status: "Documentation Required",
  },
  {
    id: "PROJ-236",
    name: "Community Solar Program",
    developer: "Solar Solutions Inc",
    submitted: "2024-01-14",
    type: "Renewable Energy",
    status: "Technical Review",
  },
]);

const recentTransfers = ref([
  {
    id: "TRF-001",
    fromCountry: "Country A",
    toCountry: "Country B",
    credits: 500,
    date: "2024-01-15",
    status: "Authorized",
  },
  {
    id: "TRF-002",
    fromCountry: "Country C",
    toCountry: "Country A",
    credits: 750,
    date: "2024-01-13",
    status: "Pending Authorization",
  },
]);
</script>

<template>
  <MemberHeader />
  <div class="flex h-screen bg-gray-50">
    <GovernmentSidebar />
    <div id="main-content" class="flex-1 flex flex-col">
      <!-- Header -->
      <header id="header" class="bg-white border-b border-light-gray p-5">
        <div class="flex justify-between items-center">
          <div>
            <h2 class="text-2xl font-medium text-dark-navy">
              {{ $t('government.dashboard_page.title') }}
            </h2>
            <p class="text-gray-600">{{ $t('government.dashboard_page.subtitle') }}</p>
          </div>
          <div class="flex items-center space-x-4">
            <button
              class="p-2 rounded-md border border-light-gray hover:bg-gray-50"
            >
              <i class="fa-solid fa-bell text-gray-600"></i>
            </button>
            <div class="flex items-center space-x-3">
              <img
                src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg"
                alt="Admin"
                class="w-8 h-8 rounded-full"
              />
              <span class="text-sm font-medium">{{ $t('government.dashboard_page.admin_user') }}</span>
            </div>
          </div>
        </div>
      </header>

      <!-- Dashboard Content -->
      <main id="dashboard-main" class="flex-1 p-5 overflow-y-auto">
        <!-- Stats Cards -->
        <div
          id="stats-section"
          class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6"
        >
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.dashboard_page.stats.pending_applications') }}</p>
                <p class="text-2xl font-medium text-dark-navy">24</p>
              </div>
              <i class="fa-solid fa-clock text-forest-green text-xl"></i>
            </div>
          </div>
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.dashboard_page.stats.certificates_issued') }}</p>
                <p class="text-2xl font-medium text-dark-navy">156</p>
              </div>
              <i class="fa-solid fa-certificate text-electric-blue text-xl"></i>
            </div>
          </div>
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.dashboard_page.stats.active_projects') }}</p>
                <p class="text-2xl font-medium text-dark-navy">89</p>
              </div>
              <i
                class="fa-solid fa-project-diagram text-forest-green text-xl"
              ></i>
            </div>
          </div>
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.dashboard_page.stats.open_grievances') }}</p>
                <p class="text-2xl font-medium text-error-red">7</p>
              </div>
              <i
                class="fa-solid fa-exclamation-triangle text-error-red text-xl"
              ></i>
            </div>
          </div>
        </div>

        <!-- Quick Actions -->
        <div
          id="quick-actions"
          class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6"
        >
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <h3 class="text-lg font-medium text-dark-navy mb-4">
              {{ $t('government.dashboard_page.quick_actions.application_queue.title') }}
            </h3>
            <div class="space-y-3">
              <div
                class="flex items-center justify-between p-3 bg-gray-50 rounded-md"
              >
                <div>
                  <p class="font-medium">PIN-2024-001</p>
                  <p class="text-sm text-gray-600">{{ $t('government.dashboard_page.quick_actions.application_queue.pin') }}</p>
                </div>
                <span
                  class="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs"
                  >{{ $t('government.dashboard_page.quick_actions.application_queue.pending') }}</span
                >
              </div>
              <div
                class="flex items-center justify-between p-3 bg-gray-50 rounded-md"
              >
                <div>
                  <p class="font-medium">PDD-2024-002</p>
                  <p class="text-sm text-gray-600">{{ $t('government.dashboard_page.quick_actions.application_queue.pdd') }}</p>
                </div>
                <span
                  class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                  >{{ $t('government.dashboard_page.quick_actions.application_queue.review') }}</span
                >
              </div>
              <div
                class="flex items-center justify-between p-3 bg-gray-50 rounded-md"
              >
                <div>
                  <p class="font-medium">AUTH-2024-003</p>
                  <p class="text-sm text-gray-600">{{ $t('government.dashboard_page.quick_actions.application_queue.authorization') }}</p>
                </div>
                <span
                  class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                  >{{ $t('government.dashboard_page.quick_actions.application_queue.approved') }}</span
                >
              </div>
            </div>
          </div>

          <div class="bg-white p-5 rounded-md border border-light-gray">
            <h3 class="text-lg font-medium text-dark-navy mb-4">
              {{ $t('government.dashboard_page.quick_actions.recent_certificates.title') }}
            </h3>
            <div class="space-y-3">
              <div
                class="flex items-center justify-between p-3 bg-gray-50 rounded-md"
              >
                <div>
                  <p class="font-medium">CR-2024-045</p>
                  <p class="text-sm text-gray-600">{{ $t('government.dashboard_page.quick_actions.recent_certificates.credit_registration') }}</p>
                </div>
                <span
                  class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                  >{{ $t('government.dashboard_page.quick_actions.recent_certificates.issued') }}</span
                >
              </div>
              <div
                class="flex items-center justify-between p-3 bg-gray-50 rounded-md"
              >
                <div>
                  <p class="font-medium">LOA-2024-046</p>
                  <p class="text-sm text-gray-600">{{ $t('government.dashboard_page.quick_actions.recent_certificates.letter_authorization') }}</p>
                </div>
                <span
                  class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                  >{{ $t('government.dashboard_page.quick_actions.recent_certificates.issued') }}</span
                >
              </div>
              <div
                class="flex items-center justify-between p-3 bg-gray-50 rounded-md"
              >
                <div>
                  <p class="font-medium">CCI-2024-047</p>
                  <p class="text-sm text-gray-600">{{ $t('government.dashboard_page.quick_actions.recent_certificates.credit_cancellation') }}</p>
                </div>
                <span
                  class="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs"
                  >{{ $t('government.dashboard_page.quick_actions.recent_certificates.processing') }}</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Registry Overview -->
        <div
          id="registry-overview"
          class="bg-white p-5 rounded-md border border-light-gray mb-6"
        >
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-medium text-dark-navy">
              {{ $t('government.dashboard_page.registry_overview.title') }}
            </h3>
            <div class="flex space-x-2">
              <select
                class="px-3 py-2 border border-light-gray rounded-md text-sm"
              >
                <option>All Projects</option>
                <option>Renewable Energy</option>
                <option>Forestry</option>
              </select>
              <select
                class="px-3 py-2 border border-light-gray rounded-md text-sm"
              >
                <option>All Status</option>
                <option>Active</option>
                <option>Pending</option>
              </select>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="p-4 bg-blue-50 rounded-md">
              <h4 class="font-medium text-dark-navy">{{ $t('government.dashboard_page.registry_overview.verified_emissions') }}</h4>
              <p class="text-2xl font-bold text-forest-green">2,450,000</p>
              <p class="text-sm text-gray-600">{{ $t('government.dashboard_page.registry_overview.tco2e') }}</p>
            </div>
            <div class="p-4 bg-green-50 rounded-md">
              <h4 class="font-medium text-dark-navy">{{ $t('government.dashboard_page.registry_overview.retired_credits') }}</h4>
              <p class="text-2xl font-bold text-forest-green">1,890,000</p>
              <p class="text-sm text-gray-600">{{ $t('government.dashboard_page.registry_overview.credits') }}</p>
            </div>
            <div class="p-4 bg-yellow-50 rounded-md">
              <h4 class="font-medium text-dark-navy">{{ $t('government.dashboard_page.registry_overview.pending_transfer') }}</h4>
              <p class="text-2xl font-bold text-electric-blue">560,000</p>
              <p class="text-sm text-gray-600">{{ $t('government.dashboard_page.registry_overview.credits') }}</p>
            </div>
          </div>
        </div>

        <!-- Administration Summary -->
        <div id="admin-summary" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <h3 class="text-lg font-medium text-dark-navy mb-4">
              {{ $t('government.dashboard_page.ndc_allocation.title') }}
            </h3>
            <div class="space-y-3">
              <div class="flex justify-between items-center">
                <span class="text-sm">{{ $t('government.dashboard_page.ndc_allocation.total_allocation') }}</span>
                <span class="font-medium">30%</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm">{{ $t('government.dashboard_page.ndc_allocation.renewable_energy') }}</span>
                <span class="font-medium">15%</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm">{{ $t('government.dashboard_page.ndc_allocation.forestry') }}</span>
                <span class="font-medium">10%</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm">{{ $t('government.dashboard_page.ndc_allocation.agriculture') }}</span>
                <span class="font-medium">5%</span>
              </div>
            </div>
          </div>

          <div class="bg-white p-5 rounded-md border border-light-gray">
            <h3 class="text-lg font-medium text-dark-navy mb-4">
              {{ $t('government.dashboard_page.buffer_account.title') }}
            </h3>
            <div class="space-y-3">
              <div class="flex justify-between items-center">
                <span class="text-sm">{{ $t('government.dashboard_page.buffer_account.national_buffer') }}</span>
                <span class="font-medium">2%</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm">{{ $t('government.dashboard_page.buffer_account.transaction_account') }}</span>
                <span class="font-medium">30%</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm">{{ $t('government.dashboard_page.buffer_account.available_credits') }}</span>
                <span class="font-medium text-forest-green">485,000</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>
